import React, {
  useMemo,
  useState,
  useEffect
} from 'react';

import {
  Box,
  Flex,
  Button,
  Paragraph,
  LoadingContainer
} from '@ubisend/pulse-components';
import {
  useQuery,
  useQueryClient
} from '@ubisend/pulse-hooks';
import { useAuth } from '@ubisend/pulse-auth';
import { usePaginationReducer } from '@ubisend/pulse-components';

import {
  fetchCategories,
  getZendeskSettings
} from '../api';
import ZendeskCategoriesList from './ZendeskCategoriesList';
import ZendeskArticlesList from './ZendeskArticlesList';
import ZendeskModalManager from './ZendeskModalManager';

const Zendesk = () => {
  const { hasPermission } = useAuth();
  const canEditResource = hasPermission('edit generative ai resource');

  const pagination = usePaginationReducer({
    pathname: '/zendesk',
    fetchQueryKey: ['generative-ai/zendesk']
  });

  const queryClient = useQueryClient();
  const [activeModal, setActiveModal] = useState(null);
  const [modalProps, setModalProps] = useState({});
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [isViewingArticles, setIsViewingArticles] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isAddCategoryLoading, setIsAddCategoryLoading] = useState(false);
  const [categoryLoadingIds, setCategoryLoadingIds] = useState({});
  const [articleLoadingIds, setArticleLoadingIds] = useState({});
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const [errorIds, setErrorIds] = useState({});
  const [shouldRefetch, setShouldRefetch] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['generative-ai/zendesk/', pagination.params],
    queryFn: () => getZendeskSettings(pagination.params),
    keepPreviousData: true,
    refetchInterval: shouldRefetch ? 5000 : false
  });

  const responseData = useMemo(() => data?.data ?? {}, [data]);
  const settings = useMemo(() => responseData.settings ?? {}, [responseData]);
  const paginatedCategories = useMemo(() => responseData.categories ?? [], [responseData]);
  const meta = useMemo(() => responseData.meta ?? [], [responseData]);
  const hasPending = useMemo(() => responseData.has_pending ?? false, [responseData]);

  useEffect(() => {
    const next = !isViewingArticles && (settings?.status === 0 || hasPending);
    setShouldRefetch(next);
  }, [hasPending, settings?.status, isViewingArticles, paginatedCategories]);

  const openModal = (type, props = {}) => {
    setActiveModal(type);
    setModalProps(props);
  };

  const closeModal = () => {
    setActiveModal(null);
    setModalProps({});
  };

  // Cleanup per-article loading when closing article view
  useEffect(() => {
    if (!isViewingArticles) {
      setArticleLoadingIds({});
    }
  }, [isViewingArticles]);

  if (isLoading) {
    return (
      <Flex col center middle tall>
        <LoadingContainer />
        <Paragraph mt>Loading Zendesk...</Paragraph>
      </Flex>
    );
  }

  return (
    <Flex col>
      {/* Initial connection UI */}
      {(!settings || Object.keys(settings).length === 0) && !isLoading && (
        <Box border="dashed">
          <Flex col middle center>
            <>
              <Paragraph mb secondary>Connect your Zendesk account to sync support data.</Paragraph>
              {canEditResource && (
                <Button
                  icon="plus"
                  variant="primary"
                  onClick={() => openModal('connect')}
                >
                  Connect Zendesk
                </Button>
              )}
            </>
          </Flex>
        </Box>
      )}

      {/* Category selection UI */}
      {settings && settings.status === 1 && Object.keys(settings).length > 0 && (!paginatedCategories || paginatedCategories.length === 0) && (
        <Box border="dashed">
          <Flex col middle center>
            <>
              <Paragraph mb secondary>Choose Zendesk Categories to Sync.</Paragraph>
              <Button
                icon="plus"
                variant="secondary"
                mt="md"
                loading={isAddCategoryLoading}
                onClick={async () => {
                  setIsAddCategoryLoading(true);
                  try {
                    const response = await fetchCategories();
                    setCategories(response.data ?? []);
                    openModal('category');
                  } finally {
                    setIsAddCategoryLoading(false);
                  }
                }}
              >
                Select Categories
              </Button>
            </>
          </Flex>
        </Box>
      )}

      {/* Disconnecting UI */}
      {settings && settings.status === 0 && (
        <Box border="dashed">
          <Flex col middle center>
            <Paragraph mb secondary>Disconnecting your Zendesk account. This may take a moment...</Paragraph>
          </Flex>
        </Box>
      )}

      {/* Categories List */}
      {settings.status === 1 && paginatedCategories.length > 0 && (
        <ZendeskCategoriesList
          categories={paginatedCategories}
          settings={settings}
          loadingIds={categoryLoadingIds}
          isSyncing={isSyncingAll}
          onDisconnect={() => openModal('confirmation', { type: 'disconnect' })}
          onView={(id) => {
            setSelectedCategoryId(id);
            setIsViewingArticles(true);
          }}
          onResync={(category) => openModal('confirmation', { type: 'resync', category })}
          onDelete={(category) => openModal('confirmation', { type: 'delete', category })}
          onAddCategory={async () => {
            setIsAddCategoryLoading(true);
            try {
              const response = await fetchCategories();
              setCategories(response.data ?? []);
              openModal('category');
            } finally {
              setIsAddCategoryLoading(false);
            }
          }}
          onResyncAll={() => openModal('confirmation', { type: 'resyncAll' })}
          isAddCategoryLoading={isAddCategoryLoading}
          pagination={{ meta, props: pagination.props }}
          hasPending={hasPending}
        />
      )}

      {/* Articles List */}
      {selectedCategoryId && (
        <ZendeskArticlesList
          categoryId={selectedCategoryId}
          handleHide={() => {
            setSelectedCategoryId(null);
            setIsViewingArticles(false);
            queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/'] });
          }}
          onOpenTagsModal={(article) => openModal('tags', { article, categoryId: selectedCategoryId })}
          onOpenRecrawlModal={(article) =>
            openModal('confirmation', { type: 'recrawl', article, categoryId: selectedCategoryId })
          }
          loadingIds={articleLoadingIds}
          errorIds={errorIds}
        />
      )}

      {/* Modal Manager */}
      <ZendeskModalManager
        activeModal={activeModal}
        modalProps={modalProps}
        closeModal={closeModal}
        categories={categories}
        setCategories={setCategories}
        setErrorIds={setErrorIds}
        setCategoryLoadingIds={setCategoryLoadingIds}
        setArticleLoadingIds={setArticleLoadingIds}
        setIsSyncingAll={setIsSyncingAll}
      />
    </Flex>
  );
};

export default Zendesk;